import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { LOCAL_STORAGE_KEYS } from '../../../core/constants/local-storage-keys';
import { BrandingService } from '../../../core/services/branding.service';
import { StorageService } from '../../../core/services/storage.service';
import { TenantService } from '../../tenant/services/tenant.service';
import { UserService } from '../../user/services/user.service';
import { ConfigService } from '../../../core/services/config.service';

export interface LoginRequest {
  userName: string;
  password: string;
}

export interface LoginResponse {
  token: string;
}

export interface LoginApplicationParams {
  data: LoginRequest;
  tenantApplicationId: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private brandingService = inject(BrandingService);
  private storageService = inject(StorageService);
  private tenantService = inject(TenantService);
  private userService = inject(UserService);
  private configService = inject(ConfigService);

  private get apiUrl(): string {
    return `${this.configService.getApiBaseUrl()}/api/Authenticator`;
  }

  /**
   * Login for management users (SysAdmin, AppAdmin, TenantAdmin)
   */
  loginManagement(credentials: LoginRequest): Observable<LoginResponse> {
    const loginData = {
      userName: credentials.userName.trim(),
      password: credentials.password,
    };
    return this.http.post<LoginResponse>(`${this.apiUrl}/LoginManagement`, loginData);
  }

  logout(): void {
    this.clearSessionCaches();
    this.storageService.removeItem(LOCAL_STORAGE_KEYS.AUTH_INFO, 'both');
    this.storageService.removeItem(LOCAL_STORAGE_KEYS.TENANT, 'both');
    this.storageService.removeItem(LOCAL_STORAGE_KEYS.SELECTED_TENANT, 'both');
    this.storageService.removeItem(LOCAL_STORAGE_KEYS.REDIRECT_URI);
    this.brandingService.clearBranding();
  }

  isAuthenticated(): boolean {
    const authInfo = this.storageService.getItem<{
      token?: string;
      Token?: string;
      accessToken?: string;
      access_token?: string;
    }>(LOCAL_STORAGE_KEYS.AUTH_INFO, 'both');

    if (!authInfo) {
      return false;
    }

    const token = authInfo.token || authInfo.Token || authInfo.accessToken || authInfo.access_token;
    return !!token;
  }

  /**
   * Save authentication info to both localStorage and sessionStorage
   * Following React implementation pattern
   */
  saveAuthInfo(token: string): void {
    this.clearSessionCaches();
    const authInfo = { token };
    this.storageService.setItem(LOCAL_STORAGE_KEYS.AUTH_INFO, authInfo, 'both');
  }

  private clearSessionCaches(): void {
    this.tenantService.clearTenantsCache();
    this.userService.clearUsersCache();
  }

  getToken(): string | null {
    const authInfo = this.storageService.getItem<{
      token?: string;
      Token?: string;
      accessToken?: string;
      access_token?: string;
    }>(LOCAL_STORAGE_KEYS.AUTH_INFO, 'both');

    if (!authInfo) {
      return null;
    }

    return (
      authInfo.token || authInfo.Token || authInfo.accessToken || authInfo.access_token || null
    );
  }
}
